import React, { useEffect, useState } from 'react';
import {
  bool, number, oneOf, oneOfType, shape, string
} from 'prop-types';
import { Image } from '@thd-olt-component-react/image';
import classNames from 'classnames';
import styles from './video-player.module.scss';
import { createPlayer, clearPlayerScripts } from '../helpers/videoPlayerHelpers';

export const VideoPlayer = ({
  analyticsData,
  aspectRatio,
  autoPlay,
  className,
  height,
  videoId,
  videoSrc,
  videoStill,
  width,
  source
}) => {
  useEffect(() => {
    LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('video-player.ready');
  });

  const [enableVideoStill, setEnableVideoStill] = useState(!!videoStill);

  const iframeID = videoSrc && `thd-youtube-iframe-${videoSrc}`;
  let player = null;
  const playerIds = {};
  const players = [];
  const videoSource = source === 'CONFIGURABLE_S3' ? videoSrc : `${videoSrc}?autoplay=true&rel=0&enablejsapi=1`;
  useEffect(() => {
    if (!enableVideoStill && videoSrc && !player) {
      if (typeof window.YT === 'undefined' || typeof window.YT.Player === 'undefined') {
        document.head.append(
          Object.assign(document.createElement('script'), {
            src: 'https://www.youtube.com/iframe_api',
          })
        );
      }

      if (window.onYouTubeIframeAPIReady) {
        window.onYouTubeIframeAPIReady();
      } else {
        window.onYouTubeIframeAPIReady = function () {
          const frames = Array(...document.getElementsByTagName('iframe')).filter(
            (frame) => frame.id && frame.id.includes('thd-youtube-iframe')
          );

          frames.forEach((frame) => {
            let newPlayer;
            if (!playerIds[frame.id]) {
              playerIds[frame.id] = true;
              newPlayer = createPlayer({ id: frame.id, analyticsData });
              players.push(newPlayer);
            }

            if (frame.id === iframeID) {
              player = newPlayer;
            }
          });
        };
      }
    }
    return () => {
      clearPlayerScripts(players);
    };
  }, [enableVideoStill]);

  const containerStyle = {
    width,
    maxWidth: width,
  };

  const videoStillWrapperStyle = classNames(
    styles['video-player__wrapper'],
    styles['video-player__wrapper--still']
  );

  const playButtonStyles = classNames(styles['video-player__play-button']);

  if (enableVideoStill) {
    return (
      <div className={styles['video-player__container']} style={containerStyle}>
        <div className={videoStillWrapperStyle}>
          <Image
            alt="thumbnail"
            className={styles['video-player__still']}
            height={height}
            src={videoStill}
            width={width}
          />
          <button
            alt="Play Button"
            aria-label="play video"
            className={playButtonStyles}
            onClick={() => setEnableVideoStill(false)}
            type="button"
          />
        </div>
      </div>
    );
  }

  if (videoSrc) {
    return (
      <div className={styles['video-player__container']} style={containerStyle}>
        <iframe
          id={iframeID}
          className={className}
          src={videoSource}
          title={videoSrc}
          allow="autoplay"
          allowFullScreen
          webkitallowfullscreen="true"
          mozallowfullscreen="true"
          height={height}
          width={width}
        />
      </div>
    );
  }

  let iframeSrc = `https://players.brightcove.net/66036796001/4JE-ofbdg_default/index.html?videoId=${videoId}`;
  iframeSrc += autoPlay || videoStill ? '&autoplay=any&playsinline=true' : '';

  const videoWrapperStyle = classNames(
    styles['video-player__wrapper'],
    styles['video-player__responsive'],
    {
      [styles['video-player__responsive--16-9']]: aspectRatio === 16 / 9,
      [styles['video-player__responsive--4-3']]: aspectRatio === 4 / 3,
    }
  );

  return (
    <div className={styles['video-player__container']} style={containerStyle}>
      <div className={videoWrapperStyle}>
        <iframe
          className={classNames(styles['video-player'], className)}
          height={height}
          src={iframeSrc}
          title={videoId}
          allowFullScreen
          webkitallowfullscreen="true"
          mozallowfullscreen="true"
          width={width}
        />
      </div>
    </div>
  );
};

VideoPlayer.propTypes = {
  analyticsData: shape({}),
  aspectRatio: oneOf([16 / 9, 4 / 3]),
  autoPlay: bool,
  className: string,
  height: number,
  videoId: oneOfType([string, number]),
  videoSrc: string,
  videoStill: string,
  width: number,
  source: string,
};

VideoPlayer.defaultProps = {
  analyticsData: null,
  aspectRatio: 16 / 9,
  autoPlay: false,
  className: null,
  height: null,
  videoId: null,
  videoSrc: null,
  videoStill: null,
  width: null,
  source: null
};
